import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import { 
    FileText, 
    Plus, 
    Eye, 
    Download,
    Navigation,
    Wind,
    Compass,
    Fuel,
    MapPin,
    Clock,
    Copy,
    Check
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${window.location.origin}/api`;

const escalaBeaufort = [
    "0 - Calmo", "1 - Bafagem", "2 - Aragem", "3 - Fraco", 
    "4 - Moderado", "5 - Fresco", "6 - Muito Fresco", "7 - Forte",
    "8 - Muito Forte", "9 - Duro", "10 - Muito Duro", "11 - Tempestuoso", "12 - Furacão"
];

const direcoesVento = ["N", "NE", "E", "SE", "S", "SW", "W", "NW"];

const BoletinsPage = () => {
    const [boletins, setBoletins] = useState([]);
    const [viagens, setViagens] = useState([]);
    const [empurradores, setEmpurradores] = useState([]);
    const [balsas, setBalsas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [viewDialogOpen, setViewDialogOpen] = useState(false);
    const [selectedBoletim, setSelectedBoletim] = useState(null);
    const [textoBoletim, setTextoBoletim] = useState('');
    const [copied, setCopied] = useState(false);
    const { hasRole } = useAuth();

    const [formData, setFormData] = useState({
        viagem_id: '',
        empurrador_id: '',
        balsa_ids: [],
        data_boletim: '',
        // Posição
        latitude_graus: '',
        latitude_minutos: '',
        latitude_hemisferio: 'S',
        longitude_graus: '',
        longitude_minutos: '',
        longitude_hemisferio: 'W',
        // Navegação
        rumo_verdadeiro: '',
        velocidade_instantanea: '',
        milhas_restantes: '',
        // Meteorologia
        vento_velocidade: '',
        vento_direcao: 'N',
        vento_beaufort: '4 - Moderado',
        // Hidrodinâmica
        corrente_direcao: '',
        swell_altura: '',
        // ETA
        eta_porto: '',
        eta_data: '',
        // Consumo
        consumo_mgo_24h: '',
        // Observações
        observacoes: ''
    });

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const [boletinsRes, viagensRes, empurradoresRes, balsasRes] = await Promise.all([
                axios.get(`${API}/boletins`),
                axios.get(`${API}/viagens?status=em_andamento`),
                axios.get(`${API}/empurradores`),
                axios.get(`${API}/balsas`)
            ]);
            setBoletins(boletinsRes.data);
            setViagens(viagensRes.data);
            setEmpurradores(empurradoresRes.data.filter(e => e.status === 'ativo'));
            setBalsas(balsasRes.data.filter(b => b.status === 'ativa'));
        } catch (error) {
            toast.error('Erro ao carregar dados');
        } finally {
            setLoading(false);
        }
    };

    const handleViagemChange = (viagemId) => {
        const viagem = viagens.find(v => v.id === viagemId);
        if (viagem) {
            setFormData(prev => ({
                ...prev,
                viagem_id: viagemId,
                empurrador_id: viagem.empurrador_id,
                balsa_ids: [
                    viagem.balsa_1_id,
                    viagem.balsa_2_id,
                    viagem.balsa_3_id,
                    viagem.balsa_4_id
                ].filter(Boolean)
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        const data = {
            ...formData,
            latitude_graus: parseFloat(formData.latitude_graus),
            latitude_minutos: parseFloat(formData.latitude_minutos),
            longitude_graus: parseFloat(formData.longitude_graus),
            longitude_minutos: parseFloat(formData.longitude_minutos),
            rumo_verdadeiro: parseFloat(formData.rumo_verdadeiro),
            velocidade_instantanea: parseFloat(formData.velocidade_instantanea),
            milhas_restantes: parseFloat(formData.milhas_restantes),
            vento_velocidade: parseFloat(formData.vento_velocidade),
            corrente_direcao: formData.corrente_direcao ? parseFloat(formData.corrente_direcao) : null,
            swell_altura: formData.swell_altura ? parseFloat(formData.swell_altura) : null,
            consumo_mgo_24h: parseFloat(formData.consumo_mgo_24h),
            data_boletim: new Date(formData.data_boletim).toISOString(),
            eta_data: new Date(formData.eta_data).toISOString()
        };
        
        try {
            await axios.post(`${API}/boletins`, data);
            toast.success('Boletim registrado com sucesso!');
            setDialogOpen(false);
            resetForm();
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erro ao registrar boletim');
        }
    };

    const handleViewBoletim = async (boletim) => {
        setSelectedBoletim(boletim);
        try {
            const response = await axios.get(`${API}/boletins/${boletim.id}/texto`);
            setTextoBoletim(response.data.texto);
            setViewDialogOpen(true);
        } catch (error) {
            toast.error('Erro ao carregar boletim');
        }
    };

    const handleCopyTexto = () => {
        navigator.clipboard.writeText(textoBoletim);
        setCopied(true);
        toast.success('Boletim copiado para a área de transferência!');
        setTimeout(() => setCopied(false), 2000);
    };

    const handleDownloadTexto = () => {
        const blob = new Blob([textoBoletim], { type: 'text/plain' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${selectedBoletim.codigo}.txt`;
        a.click();
        toast.success('Boletim baixado!');
    };

    const resetForm = () => {
        setFormData({
            viagem_id: '',
            empurrador_id: '',
            balsa_ids: [],
            data_boletim: '',
            latitude_graus: '',
            latitude_minutos: '',
            latitude_hemisferio: 'S',
            longitude_graus: '',
            longitude_minutos: '',
            longitude_hemisferio: 'W',
            rumo_verdadeiro: '',
            velocidade_instantanea: '',
            milhas_restantes: '',
            vento_velocidade: '',
            vento_direcao: 'N',
            vento_beaufort: '4 - Moderado',
            corrente_direcao: '',
            swell_altura: '',
            eta_porto: '',
            eta_data: '',
            consumo_mgo_24h: '',
            observacoes: ''
        });
    };

    const getEmpurradorNome = (id) => empurradores.find(e => e.id === id)?.nome || '-';
    const getViagemNumero = (id) => viagens.find(v => v.id === id)?.numero_viagem || '-';

    const formatDateTime = (dateStr) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleString('pt-BR');
    };

    const canEdit = hasRole(['admin', 'gestor', 'operador']);

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-pulse text-cyan-400">Carregando...</div>
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-fade-in" data-testid="boletins-page">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="font-heading text-2xl font-bold text-white tracking-tight">
                        Boletim Diário de Navegação
                    </h1>
                    <p className="text-sm text-slate-400 mt-1">
                        Emissão do boletim do meio-dia com dados operacionais e meteorológicos
                    </p>
                </div>
                
                {canEdit && viagens.length > 0 && (
                    <Dialog open={dialogOpen} onOpenChange={(open) => {
                        setDialogOpen(open);
                        if (!open) resetForm();
                    }}>
                        <DialogTrigger asChild>
                            <Button 
                                data-testid="btn-new-boletim"
                                className="bg-cyan-600 hover:bg-cyan-700 text-white rounded-sm"
                            >
                                <Plus className="w-4 h-4 mr-2" />
                                Novo Boletim
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="bg-slate-900 border-slate-800 rounded-sm max-w-4xl max-h-[90vh] overflow-y-auto">
                            <DialogHeader>
                                <DialogTitle className="font-heading text-xl text-white flex items-center gap-2">
                                    <FileText className="w-5 h-5 text-cyan-400" />
                                    Emitir Boletim Diário - Meio-Dia
                                </DialogTitle>
                            </DialogHeader>
                            
                            <form onSubmit={handleSubmit} className="space-y-6 mt-4">
                                {/* Identificação */}
                                <div className="space-y-4">
                                    <h3 className="text-sm font-mono uppercase tracking-wider text-cyan-400 border-b border-slate-800 pb-2">
                                        Identificação
                                    </h3>
                                    
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                                Viagem *
                                            </Label>
                                            <Select 
                                                value={formData.viagem_id} 
                                                onValueChange={handleViagemChange}
                                            >
                                                <SelectTrigger className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm">
                                                    <SelectValue placeholder="Selecione a viagem..." />
                                                </SelectTrigger>
                                                <SelectContent className="bg-slate-900 border-slate-800">
                                                    {viagens.map((viagem) => (
                                                        <SelectItem key={viagem.id} value={viagem.id}>
                                                            {viagem.numero_viagem} - {viagem.comandante}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div className="space-y-2">
                                            <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                                Data/Hora do Boletim *
                                            </Label>
                                            <Input
                                                type="datetime-local"
                                                value={formData.data_boletim}
                                                onChange={(e) => setFormData({...formData, data_boletim: e.target.value})}
                                                required
                                                className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Posição Geográfica */}
                                <div className="space-y-4">
                                    <h3 className="text-sm font-mono uppercase tracking-wider text-cyan-400 border-b border-slate-800 pb-2 flex items-center gap-2">
                                        <MapPin className="w-4 h-4" />
                                        Posição Geográfica
                                    </h3>
                                    
                                    <div className="grid grid-cols-2 gap-6">
                                        <div className="space-y-2">
                                            <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">Latitude</Label>
                                            <div className="flex gap-2">
                                                <Input
                                                    type="number"
                                                    placeholder="Graus"
                                                    value={formData.latitude_graus}
                                                    onChange={(e) => setFormData({...formData, latitude_graus: e.target.value})}
                                                    required
                                                    className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm w-20"
                                                />
                                                <span className="text-slate-400 self-center">°</span>
                                                <Input
                                                    type="number"
                                                    step="0.01"
                                                    placeholder="Min"
                                                    value={formData.latitude_minutos}
                                                    onChange={(e) => setFormData({...formData, latitude_minutos: e.target.value})}
                                                    required
                                                    className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm w-20"
                                                />
                                                <span className="text-slate-400 self-center">'</span>
                                                <Select 
                                                    value={formData.latitude_hemisferio} 
                                                    onValueChange={(v) => setFormData({...formData, latitude_hemisferio: v})}
                                                >
                                                    <SelectTrigger className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm w-16">
                                                        <SelectValue />
                                                    </SelectTrigger>
                                                    <SelectContent className="bg-slate-900 border-slate-800">
                                                        <SelectItem value="N">N</SelectItem>
                                                        <SelectItem value="S">S</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                        </div>
                                        <div className="space-y-2">
                                            <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">Longitude</Label>
                                            <div className="flex gap-2">
                                                <Input
                                                    type="number"
                                                    placeholder="Graus"
                                                    value={formData.longitude_graus}
                                                    onChange={(e) => setFormData({...formData, longitude_graus: e.target.value})}
                                                    required
                                                    className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm w-20"
                                                />
                                                <span className="text-slate-400 self-center">°</span>
                                                <Input
                                                    type="number"
                                                    step="0.01"
                                                    placeholder="Min"
                                                    value={formData.longitude_minutos}
                                                    onChange={(e) => setFormData({...formData, longitude_minutos: e.target.value})}
                                                    required
                                                    className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm w-20"
                                                />
                                                <span className="text-slate-400 self-center">'</span>
                                                <Select 
                                                    value={formData.longitude_hemisferio} 
                                                    onValueChange={(v) => setFormData({...formData, longitude_hemisferio: v})}
                                                >
                                                    <SelectTrigger className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm w-16">
                                                        <SelectValue />
                                                    </SelectTrigger>
                                                    <SelectContent className="bg-slate-900 border-slate-800">
                                                        <SelectItem value="E">E</SelectItem>
                                                        <SelectItem value="W">W</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Navegação */}
                                <div className="space-y-4">
                                    <h3 className="text-sm font-mono uppercase tracking-wider text-cyan-400 border-b border-slate-800 pb-2 flex items-center gap-2">
                                        <Compass className="w-4 h-4" />
                                        Dados de Navegação
                                    </h3>
                                    
                                    <div className="grid grid-cols-3 gap-4">
                                        <div className="space-y-2">
                                            <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                                Rumo Verdadeiro (°) *
                                            </Label>
                                            <Input
                                                type="number"
                                                min="0"
                                                max="360"
                                                value={formData.rumo_verdadeiro}
                                                onChange={(e) => setFormData({...formData, rumo_verdadeiro: e.target.value})}
                                                required
                                                placeholder="0-360"
                                                className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                                Vel. Instantânea (nós) *
                                            </Label>
                                            <Input
                                                type="number"
                                                step="0.1"
                                                value={formData.velocidade_instantanea}
                                                onChange={(e) => setFormData({...formData, velocidade_instantanea: e.target.value})}
                                                required
                                                placeholder="Ex: 8.5"
                                                className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                                Milhas Restantes *
                                            </Label>
                                            <Input
                                                type="number"
                                                step="0.1"
                                                value={formData.milhas_restantes}
                                                onChange={(e) => setFormData({...formData, milhas_restantes: e.target.value})}
                                                required
                                                placeholder="mn"
                                                className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Meteorologia */}
                                <div className="space-y-4">
                                    <h3 className="text-sm font-mono uppercase tracking-wider text-cyan-400 border-b border-slate-800 pb-2 flex items-center gap-2">
                                        <Wind className="w-4 h-4" />
                                        Condições Meteorológicas
                                    </h3>
                                    
                                    <div className="grid grid-cols-3 gap-4">
                                        <div className="space-y-2">
                                            <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                                Velocidade do Vento (nós) *
                                            </Label>
                                            <Input
                                                type="number"
                                                step="0.1"
                                                value={formData.vento_velocidade}
                                                onChange={(e) => setFormData({...formData, vento_velocidade: e.target.value})}
                                                required
                                                placeholder="Ex: 15"
                                                className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                                Direção do Vento *
                                            </Label>
                                            <Select 
                                                value={formData.vento_direcao} 
                                                onValueChange={(v) => setFormData({...formData, vento_direcao: v})}
                                            >
                                                <SelectTrigger className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm">
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent className="bg-slate-900 border-slate-800">
                                                    {direcoesVento.map((dir) => (
                                                        <SelectItem key={dir} value={dir}>{dir}</SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div className="space-y-2">
                                            <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                                Escala Beaufort *
                                            </Label>
                                            <Select 
                                                value={formData.vento_beaufort} 
                                                onValueChange={(v) => setFormData({...formData, vento_beaufort: v})}
                                            >
                                                <SelectTrigger className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm">
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent className="bg-slate-900 border-slate-800">
                                                    {escalaBeaufort.map((escala) => (
                                                        <SelectItem key={escala} value={escala}>{escala}</SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                                Direção da Corrente (°)
                                            </Label>
                                            <Input
                                                type="number"
                                                min="0"
                                                max="360"
                                                value={formData.corrente_direcao}
                                                onChange={(e) => setFormData({...formData, corrente_direcao: e.target.value})}
                                                placeholder="0-360"
                                                className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                                Altura do Swell (m)
                                            </Label>
                                            <Input
                                                type="number"
                                                step="0.1"
                                                value={formData.swell_altura}
                                                onChange={(e) => setFormData({...formData, swell_altura: e.target.value})}
                                                placeholder="Ex: 1.5"
                                                className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* ETA e Consumo */}
                                <div className="space-y-4">
                                    <h3 className="text-sm font-mono uppercase tracking-wider text-cyan-400 border-b border-slate-800 pb-2 flex items-center gap-2">
                                        <Clock className="w-4 h-4" />
                                        ETA e Consumo
                                    </h3>
                                    
                                    <div className="grid grid-cols-3 gap-4">
                                        <div className="space-y-2">
                                            <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                                Porto de Destino *
                                            </Label>
                                            <Input
                                                value={formData.eta_porto}
                                                onChange={(e) => setFormData({...formData, eta_porto: e.target.value})}
                                                required
                                                placeholder="Nome do porto"
                                                className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                                ETA (Data/Hora) *
                                            </Label>
                                            <Input
                                                type="datetime-local"
                                                value={formData.eta_data}
                                                onChange={(e) => setFormData({...formData, eta_data: e.target.value})}
                                                required
                                                className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                                Consumo MGO 24h (L) *
                                            </Label>
                                            <Input
                                                type="number"
                                                step="0.1"
                                                value={formData.consumo_mgo_24h}
                                                onChange={(e) => setFormData({...formData, consumo_mgo_24h: e.target.value})}
                                                required
                                                placeholder="Litros"
                                                className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Observações */}
                                <div className="space-y-2">
                                    <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                        Observações Relevantes
                                    </Label>
                                    <Textarea
                                        value={formData.observacoes}
                                        onChange={(e) => setFormData({...formData, observacoes: e.target.value})}
                                        placeholder="Pontos notáveis da navegação, condições locais, referências visuais, ocorrências operacionais..."
                                        className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm min-h-[80px]"
                                    />
                                </div>

                                <div className="flex gap-3 pt-4">
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={() => {
                                            setDialogOpen(false);
                                            resetForm();
                                        }}
                                        className="flex-1 border-slate-700 text-slate-300 hover:bg-slate-800 rounded-sm"
                                    >
                                        Cancelar
                                    </Button>
                                    <Button
                                        type="submit"
                                        data-testid="btn-save-boletim"
                                        className="flex-1 bg-cyan-600 hover:bg-cyan-700 text-white rounded-sm"
                                    >
                                        Emitir Boletim
                                    </Button>
                                </div>
                            </form>
                        </DialogContent>
                    </Dialog>
                )}
            </div>

            {/* Info Card */}
            {viagens.length === 0 && (
                <Card className="bg-yellow-500/10 border-yellow-500/30 rounded-sm">
                    <CardContent className="flex items-center gap-4 p-4">
                        <Navigation className="w-6 h-6 text-yellow-400" />
                        <div>
                            <p className="text-sm text-white font-medium">Nenhuma viagem em andamento</p>
                            <p className="text-xs text-slate-400">Para emitir um boletim, é necessário ter uma viagem ativa.</p>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="bg-slate-900/50 border-slate-800 rounded-sm">
                    <CardContent className="flex items-center gap-4 p-4">
                        <div className="p-2 bg-cyan-500/20 rounded-sm">
                            <FileText className="w-5 h-5 text-cyan-400" />
                        </div>
                        <div>
                            <p className="text-2xl font-bold text-white">{boletins.length}</p>
                            <p className="text-xs text-slate-400">Boletins Emitidos</p>
                        </div>
                    </CardContent>
                </Card>
                <Card className="bg-slate-900/50 border-slate-800 rounded-sm">
                    <CardContent className="flex items-center gap-4 p-4">
                        <div className="p-2 bg-green-500/20 rounded-sm">
                            <Navigation className="w-5 h-5 text-green-400" />
                        </div>
                        <div>
                            <p className="text-2xl font-bold text-white">{viagens.length}</p>
                            <p className="text-xs text-slate-400">Viagens Ativas</p>
                        </div>
                    </CardContent>
                </Card>
                <Card className="bg-slate-900/50 border-slate-800 rounded-sm">
                    <CardContent className="flex items-center gap-4 p-4">
                        <div className="p-2 bg-blue-500/20 rounded-sm">
                            <Clock className="w-5 h-5 text-blue-400" />
                        </div>
                        <div>
                            <p className="text-2xl font-bold text-white">12:00</p>
                            <p className="text-xs text-slate-400">Horário Padrão</p>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Table */}
            <Card className="bg-slate-900/50 border-slate-800 rounded-sm">
                <CardContent className="p-0">
                    {boletins.length === 0 ? (
                        <div className="p-12 text-center">
                            <FileText className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                            <p className="text-slate-400">Nenhum boletim emitido</p>
                        </div>
                    ) : (
                        <Table className="table-naval">
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Código</TableHead>
                                    <TableHead>Data/Hora</TableHead>
                                    <TableHead>Viagem</TableHead>
                                    <TableHead>Empurrador</TableHead>
                                    <TableHead>Posição</TableHead>
                                    <TableHead>Vel. (nós)</TableHead>
                                    <TableHead>Ações</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {boletins.map((boletim) => (
                                    <TableRow key={boletim.id} data-testid={`boletim-row-${boletim.id}`}>
                                        <TableCell className="font-mono text-cyan-400">
                                            {boletim.codigo}
                                        </TableCell>
                                        <TableCell className="text-slate-300 text-sm">
                                            {formatDateTime(boletim.data_boletim)}
                                        </TableCell>
                                        <TableCell className="text-slate-400 font-mono text-xs">
                                            {getViagemNumero(boletim.viagem_id)}
                                        </TableCell>
                                        <TableCell className="text-white">
                                            {getEmpurradorNome(boletim.empurrador_id)}
                                        </TableCell>
                                        <TableCell className="text-slate-400 font-mono text-xs">
                                            {boletim.latitude_graus}°{boletim.latitude_minutos}'{boletim.latitude_hemisferio} / {boletim.longitude_graus}°{boletim.longitude_minutos}'{boletim.longitude_hemisferio}
                                        </TableCell>
                                        <TableCell className="text-green-400 font-mono">
                                            {boletim.velocidade_instantanea}
                                        </TableCell>
                                        <TableCell>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => handleViewBoletim(boletim)}
                                                className="text-slate-400 hover:text-white hover:bg-slate-800"
                                            >
                                                <Eye className="w-4 h-4" />
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>

            {/* View Boletim Dialog */}
            <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
                <DialogContent className="bg-slate-900 border-slate-800 rounded-sm max-w-3xl max-h-[90vh]">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-xl text-white flex items-center justify-between">
                            <span className="flex items-center gap-2">
                                <FileText className="w-5 h-5 text-cyan-400" />
                                {selectedBoletim?.codigo}
                            </span>
                            <div className="flex gap-2">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={handleCopyTexto}
                                    className="border-slate-700 text-slate-300 hover:bg-slate-800 rounded-sm"
                                >
                                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                                </Button>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={handleDownloadTexto}
                                    className="border-slate-700 text-slate-300 hover:bg-slate-800 rounded-sm"
                                >
                                    <Download className="w-4 h-4" />
                                </Button>
                            </div>
                        </DialogTitle>
                    </DialogHeader>
                    
                    <div className="mt-4 overflow-y-auto max-h-[60vh]">
                        <pre className="bg-slate-950 p-4 rounded-sm text-slate-300 text-xs font-mono whitespace-pre-wrap overflow-x-auto">
                            {textoBoletim}
                        </pre>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default BoletinsPage;
